import React, { useEffect, useState } from 'react';
import { ButcherHeader } from '@components/ButcherHeader';
import { OrderProfileCard } from '@components/OrderCreation/OrderProfileCard';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import {
  faCircleCheck,
  faRepeat,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Axios } from '@helpers/ApiService';
import {
  CheckinOrderProps,
  CustomizeOrderProps,
  NewOrderInitiationProps,
  OrderSpecies,
  OrderSpeciesData,
  RepeatCustomizationProps,
  Species,
} from '@helpers/Models';
import { useToast } from '@contexts/Toast';
import {
  errorMessage,
  Length,
  orderBreadCrumbSteps,
  OrderCreationMasterData,
  quantityCount,
} from '@helpers/Constants';
import { CustomerOrderService } from '@services/CustomerService';
import { RepeatCustomizationBottomSheet } from '@components/BottomSheet/RepeatCustomizationBottomSheet';
import { OrderBreadcrumb } from '@components/OrderCreation/OrderBreadcrumbs';

export const SpeciesCustomisation: React.FC = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  const [customerId, setCustomerId] = useState<number>(0);
  const [species, setSpecies] = useState<OrderSpecies[]>([]);
  const [orderData, setOrderData] = useState<CustomizeOrderProps>({
    customerId: 0,
    speciesId: 0,
    quantity: 0,
    butcherSpeciesId: 0,
    speciesName: '',
    species: [],
  });
  const [speciesList, setSpeciesList] = useState<
    {
      speciesData: OrderSpecies;
      speciesNumber: number;
      currentCustomizingId: number;
      uniqueKey: string;
    }[]
  >([]);
  const [quantity, setQuantity] = useState(orderData.quantity);
  // const [currentCustomizingId, setCurrentCustomizingId] = useState<number>(0);
  const [completionStatusMap, setCompletionStatusMap] = useState<
    Record<string | number, boolean>
  >({});
  const [openRepeatCustomizationSheet, setOpenRepeatCustomizationSheet] =
    useState<boolean>(false);
  const [remainingCustomizations, setRemainingCustomizations] = useState<
    RepeatCustomizationProps[]
  >([]);
  const [customerDetails, setCustomerDetails] =
    useState<NewOrderInitiationProps>();
  const [searchTerm, setSearchTerm] = useState('');
  const [customizeId, setCustomizeId] = useState<number>();
  const [checkinData, setCheckinData] = useState<CheckinOrderProps>();

  /**
   * useEffect hook to initialize customer ID from localStorage on component mount.
   *
   * - Retrieves the `customerDetails` object stored in localStorage.
   * - If found, parses the object and updates the `customerId` state.
   * - If not found, redirects the user to the customer list page.
   *
   * Dependencies: []
   * This effect runs only once on initial render.
   */
  useEffect(() => {
    const customerID = CustomerOrderService.getCustomerId;
    const customerDetails = CustomerOrderService.getCustomerDetails();
    setCustomerDetails(customerDetails);
    if (customerID) {
      setCustomerId(customerID);
    } else {
      navigate(FrontendRoutes.CUSTOMER_LIST);
    }
  }, []);

  /**
   * useEffect hook to retrieve order data when a valid customer ID is available.
   *
   * - This effect is triggered whenever the `customerId` state changes.
   * - If a valid `customerId` exists, it calls `getOrderData()` to fetch the associated order
   *   from localStorage and update the `orderData` state accordingly.
   *
   * Dependencies: [customerId]
   */
  useEffect(() => {
    if (customerId) {
      getOrderData();
    }
  }, [customerId]);

  /**
   * useEffect hook to fetch species master data based on the selected species ID.
   *
   * - Watches for changes to the `orderData` object.
   * - When `orderData.speciesId` is available, it triggers `fetchSpeciesMaster()` to fetch and filter species data.
   *
   * Dependencies: [orderData]
   * This effect re-runs whenever `orderData` changes.
   */
  useEffect(() => {
    if (orderData?.speciesId) {
      fetchSpeciesMaster();
    }
  }, [orderData]);

  useEffect(() => {
    const parsedOrderData = CustomerOrderService.getParsedOrderData();
    const customerId = CustomerOrderService.getCustomerId();
    const currentCustomerOrderData = parsedOrderData[customerId];

    const completionMap: Record<string | number, boolean> = {};

    currentCustomerOrderData?.species?.forEach(
      (
        speciesItem: {
          customizingId: any;
          isCustomizationCompleted: number;
        },
        index: any,
      ) => {
        const isCompleted =
          speciesItem.isCustomizationCompleted == 1 ? true : false;
        const key = speciesItem.customizingId || index;
        completionMap[key] = isCompleted;
      },
    );

    currentCustomerOrderData?.species?.some(
      (speciesItem: {
        checkIn: object;
        products: string[];
        customizingId: number;
      }) => {
        const checkIn = speciesItem.checkIn as CheckinOrderProps | undefined;

        // Check if checkIn exists and have feet & inches
        if (checkIn && checkIn.feet !== null && checkIn.inches !== null) {
          setCheckinData(checkIn);
          return true;
        }
        return false;
      },
    );

    setCompletionStatusMap(completionMap);
  }, [orderData]);

  /**
   * Retrieves the order data for the current customer from localStorage and updates the relevant state.
   *
   * - Fetches the `orderData` object from localStorage.
   * - Parses the data and extracts the order specific to the current `customerId`.
   * - Sets both `quantity` and `orderData` state values using the retrieved data.
   */
  const getOrderData = (): void => {
    const parsedOrderData = CustomerOrderService.getParsedOrderData();
    if (parsedOrderData && customerId) {
      const customerOrder = parsedOrderData[customerId];
      setQuantity(customerOrder.quantity);
      setOrderData(customerOrder);
    }
  };

  /**
   * Fetches species master data from the server and updates the local species state.
   *
   * - Sends a POST request to the `FETCH_SPECIES_MASTERDATA` API with `masterDataType` set to `1`.
   * - Filters the response to find the species that matches the current `orderData.speciesId`.
   * - Updates the local `species` state with the filtered result.
   *
   * Error Handling:
   * - If the request fails, a toast message is shown using a generic fetch error.
   *
   * Note:
   * - Assumes `orderData` is already populated and contains a valid `speciesId`.
   * - This function is typically triggered by a `useEffect` when `orderData` is available.
   */
  const fetchSpeciesMaster = async () => {
    try {
      // Fetch species from the server
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.SPECIES_MASTER_DATA,
      });
      const speciesData = response.data.data.masterData;
      const filteredSpecies = speciesData.filter(
        (species: Species) => species.id === orderData?.speciesId,
      );
      setSpecies(filteredSpecies);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Syncs the `speciesList` state whenever `species` or `quantity` changes.
   *
   * - Runs when `species` or `quantity` updates.
   * - If both `species` and `quantity` are available and valid (>0),
   *   it generates an initial list of species items based on the quantity.
   * - Each item includes:
   *   - `speciesData`: The first species in the `species` array.
   *   - `speciesNumber`: A 1-based index representing the sequence number.
   *   - `currentCustomizingId`: A 0-based index for customization tracking.
   *   - `uniqueKey`: A unique identifier composed of species ID and index.
   * - Updates the `speciesList` state with the newly generated list.
   *
   * @effect
   * @dependency species, quantity
   */
  useEffect(() => {
    if (species.length > 0 && quantity > 0) {
      const initialList = [...Array(quantity)].map((_, index) => ({
        speciesData: species[0],
        speciesNumber: index + 1,
        currentCustomizingId: index,
        uniqueKey: `${species[0].id}-${index}`,
      }));
      setSpeciesList(initialList);
    }
  }, [species, quantity]);

  /**
   * Decreases the quantity of the species being selected by 1.
   * Ensures that the quantity of species cannot go below 1.
   *
   * Purpose:
   * - This function is used to decrease the `quantity` of a species when the user wants to remove one instance from their selection.
   * - The `orderData` state and the `quantity` state are both updated to reflect this change.
   *
   * Behavior:
   * - Updates the `orderData` state by decreasing the quantity by 1.
   * - The `quantity` state is also updated, ensuring that it never drops below 1.
   */
  const handleDelete = (index: number): void => {
    // Check if species customization is completed
    if (
      completionStatusMap != null &&
      Object.keys(completionStatusMap).length > 0
    ) {
      // Update speciesList for displaying the list
      setSpeciesList((prev) =>
        prev
          .filter((_, i) => i !== index)
          .map((item, newIndex) => ({
            ...item,
            speciesNumber: newIndex + 1, // also fix numbering for UI list
          })),
      );
      // Update the quantity
      setQuantity((prev) => Math.max(prev - 1, 1));

      // Update localStorage with the modified record
      const customerId = CustomerOrderService.getCustomerId();
      const orderDataPayload = CustomerOrderService.getParsedOrderData();

      if (customerId && orderDataPayload[customerId]) {
        // Remove the species at the given index
        const updatedSpecies = orderDataPayload[customerId].species
          ?.filter((_: number, i: number) => i !== index)
          // Renumber remaining species names in sequence
          .map((item: OrderSpeciesData, newIndex: number) => ({
            ...item,
            speciesName: `${item.speciesName?.split(' - ')[0] ?? 'Species'} - ${newIndex + 1}`, // Changing the speciesName
          }));

        // Update the customer's order data with new species list and reduced quantity
        orderDataPayload[customerId] = {
          ...orderDataPayload[customerId],
          species: updatedSpecies,
          quantity: orderDataPayload[customerId].quantity - 1, // Decrement quantity since one was removed
        };

        localStorage.setItem('orderData', JSON.stringify(orderDataPayload));
      }
    } else {
      // Return if the quantity is less than 1
      setQuantity((prev) => {
        if (prev <= 1) {
          return 1;
        }
        // Remove and renumber the speciesName from speciesList
        setSpeciesList((prevList) =>
          prevList
            .filter((_, i) => i !== index)
            .map((item, newIndex) => ({
              ...item,
              speciesNumber: newIndex + 1,
            })),
        );

        return prev - 1;
      });
    }
  };

  /**
   * Updates the quantity of the current order for a specific customer.
   *
   * This function increments the local `quantity` state and updates the corresponding
   * customer's order data in `localStorage`. It expects `customerId` and `quantity`
   * to be accessible in the same scope.
   *
   * @param {number} value - The amount to increment (or decrement) the quantity by.
   *
   * Side Effects:
   * - Updates the `quantity` state using `setQuantity`.
   * - Reads from and writes to `localStorage` under the key `orderData`.
   *
   */
  const updateQuantity = (value: number): void => {
    setQuantity((prev) => {
      // Ensure the new value is at least 1
      const updatedQuantity = Math.max(prev + value, 1);

      // Update order data only if the quantity remains >= 1
      // Get existing order data
      const parsedOrderData = CustomerOrderService.getParsedOrderData();
      const customerId = CustomerOrderService.getCustomerId();
      const currentCustomerOrderData = parsedOrderData[customerId];

      parsedOrderData[customerId] = {
        ...currentCustomerOrderData,
        quantity: updatedQuantity,
      };

      localStorage.setItem('orderData', JSON.stringify(parsedOrderData));

      return updatedQuantity;
    });
  };

  /**
   * Handles the repeat customization flow for a species.
   *
   * Purpose:
   * - Identifies which species are still incomplete (not fully customized).
   * - Stores the index of the species whose customization data will be repeated.
   * - Prepares the list of remaining species that can be customized.
   * - Opens the repeat customization bottom sheet for user selection.
   *
   * @param {number} selectedIndex - The index of the species whose data should be repeated.
   *
   */
  const handleRepeatCustomization = (selectedIndex: number): void => {
    // Get only species that are not yet completed
    const isCompleted = speciesList.filter(
      (item) => !completionStatusMap[item.currentCustomizingId],
    );
    // Store the index of the species whose data will be repeated
    setCustomizeId(selectedIndex);

    // Save the remaining (incomplete) species for selection
    setRemainingCustomizations(isCompleted);
    setOpenRepeatCustomizationSheet(true);
  };

  /**
   * Applies the selected species customization data to multiple indices in orderData.
   *
   * Purpose:
   * - Copies the customization data from a source species (`customizeId`).
   * - Inserts or updates this data for each selected index in the customer's species array.
   * - Ensures that the species array is properly expanded (no `null` gaps).
   * - Persists the updated order data to localStorage and refreshes it in state.
   *
   * @param {number[]} selectedIds - Array of indices where the customization should be applied.
   *
   */
  const handleConfirmSelection = (selectedIds: number[]): void => {
    // customizeId - index of the species data that will be repeated
    if (customizeId !== undefined) {
      // Get the species data that should be copied
      const customizedData = orderData?.species?.[customizeId];
      if (!customizedData) return;

      // Get customer-specific order data from localStorage
      const customerId = CustomerOrderService.getCustomerId();
      const orderDataPayload = CustomerOrderService.getParsedOrderData();

      // Ensure the customer has an orderData entry
      if (!orderDataPayload[customerId]) {
        orderDataPayload[customerId] = { species: [] };
      }

      // Get the species array of the customer's order data
      const speciesArray = orderDataPayload[customerId].species;

      const selectedSpeciesName = localStorage.getItem('speciesName');

      // For each selected index, repeat the customizedData
      selectedIds.forEach((id) => {
        // Expand species array if index doesn’t exist yet
        while (speciesArray.length <= id) {
          speciesArray.push({}); // push empty object instead of null
        }

        // Copy the data and assign the speciesName with index
        speciesArray[id] = {
          ...customizedData,
          speciesName: `${selectedSpeciesName} - ${id + 1}`,
        };
      });

      localStorage.setItem('orderData', JSON.stringify(orderDataPayload));

      // Refresh the orderData
      getOrderData();
    }
  };

  /**
   * Handles the customization of a selected species item.
   *
   * Purpose:
   * - Constructs and stores order data for the current customer based on the selected species.
   * - Sets up required data in `localStorage` to be used on the check-in page.
   * - Navigates the user to the check-in screen.
  
   * Parameters:
   * @param {number} speciesNumber - The 1-based index of the species item being customized.
   * @param {string} speciesName - The name of the selected species.
   * @param {number} currentCustomizingId - The index ID representing the specific customization session.
  
   * Behavior:
   * - Reads existing `orderData` from localStorage.
   * - Constructs a payload with `customerId`, `speciesId`, `quantity`, and species name formatted with its index.
   * - Updates or appends this data under the customer's ID in `orderData`.
   * - Saves updated order data and the current customization ID to localStorage.
   * - Redirects the user to the `CREATE_ORDER_CHECKIN` route for customization.
   */
  const handleCustomize = (
    speciesNumber: number,
    speciesName: string,
    currentCustomizingId: number,
  ) => {
    localStorage.setItem(
      'currentCustomizingId',
      currentCustomizingId.toString(),
    );
    localStorage.setItem('speciesName', speciesName);
    // Get existing order data
    const orderDataPayload = CustomerOrderService.getParsedOrderData();

    const payload = {
      customerId: customerId,
      speciesId: orderData.speciesId,
      quantity: quantity,
      species: [
        {
          speciesName: `${speciesName} - ${speciesNumber}`,
        },
      ],
    };

    const customerData = orderDataPayload[customerId];

    let updatedSpecies: unknown[] = [];
    if (typeof customerData.species !== 'undefined') {
      updatedSpecies = [...customerData.species];
    } else {
      updatedSpecies = [];
    }

    orderDataPayload[customerId] = {
      ...customerData,
      ...payload,
      species: updatedSpecies,
      // services: updatedServices,
    };

    localStorage.setItem('orderData', JSON.stringify(orderDataPayload));
    localStorage.setItem(
      'currentCustomizingId',
      JSON.stringify(currentCustomizingId),
    );
    navigate(FrontendRoutes.CREATE_ORDER_CHECKIN);
  };

  return (
    <div className="flex h-screen w-screen">
      {/* Sidebar */}
      <SideNav active={2} />
      {/* Main Content */}
      <div className="flex-1 overflow-auto relative">
        <div>
          {/* Header */}
          <ButcherHeader />
          <OrderBreadcrumb
            steps={orderBreadCrumbSteps}
            customerName={`${customerDetails?.firstName} ${customerDetails?.lastName}`}
            activeItem="New Order"
            customerId={customerId}
          />
        </div>
        <div className="p-3 pb-16">
          {/* Customer Profile Card */}
          <OrderProfileCard />
          {/* Title */}
          <div className="flex mt-4 p-1 items-center justify-between">
            <h2 className="text-xl font-semibold">Species Customization</h2>
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                className="border p-1 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded-md"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 right-2 flex items-center">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="text-gray-500 text-sm"
                />
              </div>
            </div>
          </div>
          {/* Species and its Quantity */}
          <div className="flex justify-between mt-4 mb-3">
            <h2 className="text-lg font-medium mb-2 text-[#43474E]">
              {species[0]?.name}
            </h2>
            <div className="flex items-center space-x-3">
              <span className="font-semibold text-[#1C1B1F]">Quantity:</span>
              <div className="flex items-center">
                <button
                  onClick={() => updateQuantity(quantityCount.decrement)}
                  type="button"
                  className="w-12 h-8 flex items-center justify-center border rounded-l text-white text-lg bg-primary hover:bg-primary"
                >
                  -
                </button>
                <input
                  type="text"
                  className="w-20 h-8 text-center border-t border-b"
                  value={quantity}
                  readOnly
                />
                <button
                  onClick={() => updateQuantity(quantityCount.increment)}
                  type="button"
                  className="w-12 h-8 flex items-center justify-center border rounded-r text-white text-lg bg-primary hover:bg-primary"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {/* Species card */}
          {speciesList
            .filter(({ speciesNumber }) =>
              searchTerm.trim() === ''
                ? true
                : speciesNumber.toString().includes(searchTerm.trim()),
            )
            .map(
              (
                { speciesData, speciesNumber, uniqueKey, currentCustomizingId },
                index,
              ) => {
                const isCompleted = completionStatusMap[currentCustomizingId];
                return (
                  <div
                    key={uniqueKey}
                    className="flex p-4 border rounded-lg shadow-sm bg-white w-full mt-5"
                  >
                    <div className="w-[85px] h-[80px] overflow-hidden border border-[#D4D4D4] rounded-md">
                      <img
                        src={speciesData.imageUrl}
                        alt={speciesData.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex flex-col flex-1 px-4 justify-between">
                      <div className="flex items-start justify-between mb-4">
                        <div className="text-gray-800 font-medium">
                          {speciesData.name} - {speciesNumber}
                          {checkinData &&
                            checkinData.inches !== undefined &&
                            checkinData.feet !== undefined && (
                              <>
                                {' '}
                                ({checkinData.feet} {Length.FEET}{' '}
                                {checkinData.inches} {Length.INCHES})
                              </>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                          {isCompleted && (
                            <button
                              onClick={() =>
                                handleRepeatCustomization(currentCustomizingId)
                              }
                              type="button"
                              className="text-primary font-semibold flex items-center gap-2"
                            >
                              <FontAwesomeIcon icon={faRepeat} />
                              <span className="text-sm">
                                Repeat Customization
                              </span>
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(index)}
                            type="button"
                            className="w-[32px] h-[32px] flex items-center justify-center border border-[#D4D4D4] rounded-md text-[#4D545C] hover:text-gray-800"
                          >
                            <FontAwesomeIcon icon={faTrashCan} />
                          </button>
                        </div>
                      </div>
                      {isCompleted ? (
                        <div className="w-full">
                          <span className="inline-block w-full bg-[#CCEECC] p-2 text-[#2D5B4A] text-center font-semibold rounded-md">
                            Customization Completed{' '}
                            <FontAwesomeIcon
                              icon={faCircleCheck}
                              className="text-[#33795F]"
                            />
                          </span>
                        </div>
                      ) : (
                        <button
                          onClick={() =>
                            handleCustomize(
                              speciesNumber,
                              speciesData.name,
                              currentCustomizingId,
                            )
                          }
                          type="button"
                          className="border border-transparent text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary py-2 rounded-md w-full"
                        >
                          Add Order Details
                        </button>
                      )}
                    </div>
                  </div>
                );
              },
            )}
          {openRepeatCustomizationSheet && (
            <RepeatCustomizationBottomSheet
              onClose={() => setOpenRepeatCustomizationSheet(false)}
              speciesList={remainingCustomizations}
              onConfirmSelection={handleConfirmSelection}
            />
          )}
          {/* The following code is for customization completed UI. Do not remove this till species summary is done.*/}

          {/* <div className="flex items-center justify-between p-4 border rounded-lg shadow-md mb-4 bg-white w-full">
            <div className="w-[85px] h-[80px] overflow-hidden border border-[#D4D4D4] rounded-md">
              <img
                src="https://wildgamebutcher-dev.s3.us-east-1.amazonaws.com/species/rabbit.jpg"
                alt="Alligator"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-1 flex-col px-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-700 font-medium">Alligator - 4</span>
                <div className="flex items-center">
                  <button type="button" className="text-primary font-semibold">
                    <FontAwesomeIcon icon={faPen} /> Edit
                  </button>
                  <button
                    type="button"
                    className="ml-3 w-[32px] h-[32px] flex items-center justify-center border border-[#D4D4D4] rounded-md text-[#4D545C] hover:text-gray-800"
                  >
                    <FontAwesomeIcon icon={faTrashCan} />
                  </button>
                </div>
              </div>
              <div className='w-full mt-4'>
                <span className='inline-block w-full bg-[#CCEECC] p-2 text-[#2D5B4A] text-center font-semibold rounded-md'>
                  Customisation Completed <FontAwesomeIcon icon={faCircleCheck} className='text-[#33795F]' />
                </span>
              </div>
            </div>
          </div> */}

          <div className="flex justify-between gap-4 fixed bottom-0 left-14 w-[calc(100%-64px)] p-4">
            <button
              onClick={() => navigate('/create-order/')}
              type="button"
              className="w-[50%] px-6 py-1 border border-primary rounded-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Back
            </button>
            <button
              onClick={() => navigate(FrontendRoutes.CREATE_ORDER_SUMMARY)}
              type="button"
              className="w-[50%] px-6 py-2 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
            >
              <span>View Order Summary</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
